import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Aviso, Botao, useDialogo, type OpcoesDeConfirmacao } from './ui';
import { mensagemDoErro } from '../servicos/erros';
import { executarEmLote } from '../servicos/lote';

/** O que um lote fez: quantos deram certo e, um a um, os que falharam e por quê. */
export interface DesfechoDoLote {
  readonly feitos: number;
  readonly falhas: readonly { readonly nome: string; readonly motivo: string }[];
}

/** Uma ação em lote pronta para a barra da seleção (`BotaoDoLote`) e para o `AvisoDoLote`. */
export interface AcaoEmLote<T> {
  readonly executar: (itens: readonly T[]) => Promise<void>;
  readonly executando: boolean;
  readonly desfecho: DesfechoDoLote | null;
  readonly limparDesfecho: () => void;
}

/**
 * A ação de uma linha (excluir, levar ao CRM…) feita sobre as linhas marcadas
 * na tabela (`SelecaoDaTabela`).
 *
 * Vai item a item, pela mesma rota da ação de uma linha só: o que o servidor
 * recusaria numa linha ele recusa no lote, com o mesmo motivo — e o desfecho
 * diz quais e por quê, em vez de um erro único para o lote inteiro.
 */
export function useAcaoEmLote<T>(opcoes: {
  readonly acao: (item: T) => Promise<unknown>;
  /** Como o item aparece na lista de falhas. */
  readonly nome: (item: T) => string;
  /** Depois do lote, tenha dado certo ou não: recarregar o que mudou. */
  readonly aoTerminar?: () => void;
}): AcaoEmLote<T> {
  const [desfecho, setDesfecho] = useState<DesfechoDoLote | null>(null);
  const mutacao = useMutation({
    mutationFn: (itens: readonly T[]) => executarEmLote(itens, opcoes.acao),
    onSuccess: (r) =>
      setDesfecho({
        feitos: r.feitos.length,
        falhas: r.falhas.map((f) => ({
          nome: opcoes.nome(f.item),
          motivo: mensagemDoErro(f.erro),
        })),
      }),
    onSettled: () => opcoes.aoTerminar?.(),
  });

  return {
    executar: async (itens) => {
      setDesfecho(null);
      await mutacao.mutateAsync(itens);
    },
    executando: mutacao.isPending,
    desfecho,
    limparDesfecho: () => setDesfecho(null),
  };
}

/**
 * O botão de uma ação em lote, para a barra da seleção: pede a confirmação,
 * faz, e desmarca as linhas.
 */
export function BotaoDoLote<T>(props: {
  readonly rotulo: string;
  readonly marcadas: readonly T[];
  readonly limpar: () => void;
  readonly lote: AcaoEmLote<T>;
  readonly perigo?: boolean;
  /** O diálogo antes de agir — sem ele, age direto. */
  readonly confirmacao?: (marcadas: readonly T[]) => OpcoesDeConfirmacao;
}): JSX.Element {
  const dialogo = useDialogo();
  return (
    <Botao
      variante={props.perigo === true ? 'perigo' : 'secundario'}
      tamanho="pequeno"
      carregando={props.lote.executando}
      onClick={() => {
        void (async () => {
          if (props.confirmacao !== undefined) {
            const ok = await dialogo.confirmar(props.confirmacao(props.marcadas));
            if (!ok) return;
          }
          try {
            await props.lote.executar(props.marcadas);
          } finally {
            props.limpar();
          }
        })();
      }}
    >
      {props.rotulo}
    </Botao>
  );
}

/** O desfecho do lote, na tela: tudo certo, ou o que falhou, item a item. */
export function AvisoDoLote(props: {
  readonly desfecho: DesfechoDoLote | null;
  /** O que aconteceu com os que deram certo: "3 empresas excluídas". */
  readonly feitos: (n: number) => string;
  readonly aoFechar: () => void;
}): JSX.Element | null {
  const d = props.desfecho;
  if (d === null) return null;

  if (d.falhas.length === 0) {
    return (
      <Aviso tom="positivo" aoFechar={props.aoFechar}>
        {props.feitos(d.feitos)}.
      </Aviso>
    );
  }

  const naoDeram = d.falhas.length === 1 ? '1 não deu certo' : `${d.falhas.length} não deram certo`;
  return (
    <Aviso
      tom="atencao"
      titulo={d.feitos === 0 ? `Nenhum deu certo` : `${props.feitos(d.feitos)} — ${naoDeram}`}
      aoFechar={props.aoFechar}
    >
      <ul className="flex list-disc flex-col gap-0.5 pl-5">
        {d.falhas.map((f, i) => (
          <li key={i}>
            <span className="font-medium">{f.nome}</span>: {f.motivo}
          </li>
        ))}
      </ul>
    </Aviso>
  );
}
